<template>
  <div class="form-container pa-4">
    <v-snackbar v-model="msg" top :color="snackbarColor" :timeout="4000">
      {{ text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="msg = false"> Close </v-btn>
      </template>
    </v-snackbar>

    <v-card elevation="2" class="form-card">
      <div class="sticky-header">
        <div class="header-gradient white--text px-4">
          <div class="d-flex align-center justify-space-between w-100">
            <div>
              <v-btn
                color="white"
                text
                @click="goBack"
                class="px-2 my-2"
                elevation="0"
              >
                <v-icon left size="20">mdi-arrow-left</v-icon>
                <span class="font-weight-medium">Back</span>
              </v-btn>
            </div>

            <div>
              <v-btn
                v-if="!isViewMode"
                color="success"
                :loading="isSubmitting"
                :disabled="!isFormValid"
                @click="submitForm"
                class="px-4"
                elevation="1"
              >
                <v-icon left size="20" class="mr-1">
                  {{ submitIcon }}
                </v-icon>
                <span class="font-weight-medium">{{ submitButtonText }}</span>
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <v-card-text class="pt-6">
        <v-container>
          <v-row>
            <v-col cols="12" class="section-title pb-2">
              Basic Information
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.Name"
                label="Company Name*"
                :rules="[(v) => !!v || 'Company name is required']"
                :disabled="isViewMode"
                :error-messages="errors.Name"
                outlined
                dense
              ></v-text-field>

              <v-text-field
                v-model="formData.Alias"
                label="Alias"
                :disabled="isViewMode"
                outlined
                dense
                class="mt-4"
              ></v-text-field>

              <v-textarea
                v-model="formData.Description"
                label="Description"
                :disabled="isViewMode"
                outlined
                auto-grow
                rows="2"
                class="mt-4"
              ></v-textarea>
            </v-col>

            <v-col cols="12" md="6">
              <div class="logo-upload-container">
                <v-img
                  v-if="previewUrl || formData.Logo"
                  :src="previewUrl || formData.Logo"
                  max-width="200"
                  contain
                  class="mb-3 mx-auto"
                ></v-img>

                <div v-else class="empty-logo-placeholder mb-3">
                  <v-icon size="48" color="grey lighten-1"
                    >mdi-image-outline</v-icon
                  >
                  <div class="caption text-grey">No logo uploaded</div>
                </div>

                <v-file-input
                  v-if="!isViewMode"
                  v-model="logoFile"
                  accept="image/*"
                  label="Upload Company Logo"
                  prepend-icon="mdi-camera"
                  @change="handleLogoChange"
                  :disabled="isViewMode"
                  outlined
                  dense
                  :rules="[
                    (v) =>
                      !v ||
                      v.size < 5000000 ||
                      'Logo size should be less than 5 MB!',
                    (v) =>
                      !v ||
                      v.type.startsWith('image/') ||
                      'Please upload an image file',
                  ]"
                  hide-details
                  class="logo-input"
                ></v-file-input>
              </div>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12" class="section-title pb-2">
              Location & Contact
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.Address"
                label="Address"
                :disabled="isViewMode"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.City"
                label="City"
                :disabled="isViewMode"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="formData.State"
                :items="states"
                label="State"
                :disabled="isViewMode"
                outlined
                dense
              ></v-select>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.Zip"
                label="Zip Code"
                :disabled="isViewMode"
                outlined
                dense
                :rules="[
                  (v) => !v || /^\d{5}(-\d{4})?$/.test(v) || 'Invalid zip code',
                ]"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.Phone"
                label="Phone"
                :disabled="isViewMode"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.Email"
                label="Email"
                :disabled="isViewMode"
                outlined
                dense
                :rules="[(v) => !v || /.+@.+\..+/.test(v) || 'Invalid email']"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12" class="section-title pb-2">
              Business Information
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.ProviderID"
                label="Provider ID"
                :disabled="isViewMode"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.Vendor"
                label="EVV Vendor"
                :disabled="isViewMode"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.EIN"
                label="EIN"
                :disabled="isViewMode"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.MailingAddress"
                label="Mailing Address"
                :disabled="isViewMode"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="formData.Payroll"
                :items="payrolls"
                label="Payroll Provider"
                :disabled="isViewMode"
                outlined
                dense
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="formData.Status"
                :items="statusOptions"
                label="Status"
                :disabled="isViewMode"
                outlined
                dense
              ></v-select>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showConfirmDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm Action</v-card-title>
        <v-card-text>{{ confirmDialogMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showConfirmDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmAction">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
const url = require("@/client_config/config").url;

export default {
  name: "CompanyForm",
  props: {
    action: {
      type: String,
      required: true,
      validator: (value) => ["add", "edit", "view"].includes(value),
    },
    id: {
      type: String,
      required: false,
    },
  },

  data: () => ({
    msg: false,
    text: "",
    snackbarColor: "success",
    isLoading: false,
    isSubmitting: false,
    headerIcon: "mdi-domain",
    submitIcon: "mdi-check",
    showConfirmDialog: false,
    confirmDialogMessage: "",
    confirmActionCallback: null,
    errors: {},
    initialFormData: null,
    logoFile: null,
    previewUrl: null,
    formData: {
      Name: "",
      Description: "",
      Alias: "",
      City: "",
      State: "",
      Zip: "",
      Address: "",
      Phone: "",
      Email: "",
      Status: "",
      ProviderID: "",
      Vendor: "",
      EIN: "",
      MailingAddress: "",
      Payroll: "",
      Logo: "",
    },
    payrolls: ["Paychex", "Workstream", "Other"],
    states: ["Utah", "Nevada"],
    statusOptions: ["Active", "Inactive"],
  }),

  computed: {
    isViewMode() {
      return this.action === "view";
    },
    formTitle() {
      const titles = {
        add: "New Company",
        edit: "Edit Company",
        view: "Company Details",
      };
      return titles[this.action];
    },
    submitButtonText() {
      return this.action === "edit" ? "Update Company" : "Create Company";
    },
    isFormValid() {
      return this.formData.Name && Object.keys(this.errors).length === 0;
    },
    hasChanges() {
      return (
        JSON.stringify(this.initialFormData) !== JSON.stringify(this.formData)
      );
    },
  },

  created() {
    if (this.id && this.action !== "add") {
      this.fetchCompany();
    }
  },

  methods: {
    validateForm() {
      this.errors = {};
      if (!this.formData.Name) {
        this.errors.Name = "Company name is required";
      }
      if (this.formData.Email && !/.+@.+\..+/.test(this.formData.Email)) {
        this.errors.Email = "Invalid email format";
      }
      return Object.keys(this.errors).length === 0;
    },

    async fetchCompany() {
      this.isLoading = true;
      try {
        const response = await axios.get(`${url}/companies/${this.id}`);
        const company = response.data;
        this.formData = {
          Name: company.Name || "",
          Description: company.Description || "",
          Alias: company.Alias || "",
          City: company.City || "",
          State: company.State || "",
          Zip: company.Zip || "",
          Address: company.Address || "",
          Phone: company.Phone || "",
          Email: company.Email || "",
          Status: company.Status || "",
          ProviderID: company.ProviderID || "",
          Vendor: company.Vendor || "",
          EIN: company.EIN || "",
          MailingAddress: company.MailingAddress || "",
          Payroll: company.Payroll || "",
          Logo: company.Logo || "",
        };
        this.initialFormData = { ...this.formData };
      } catch (error) {
        this.showError("Error loading company data");
        console.error("Error fetching company:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async submitForm() {
      if (!this.validateForm()) {
        this.showError("Please fix the form errors before submitting");
        return;
      }

      this.isSubmitting = true;
      try {
        const formData = new FormData();

        Object.keys(this.formData).forEach((key) => {
          if (this.formData[key] !== undefined && this.formData[key] !== null) {
            formData.append(key, this.formData[key]);
          }
        });

        if (this.logoFile) {
          formData.append("logo", this.logoFile);
        }

        const request =
          this.action === "edit"
            ? axios.put(`${url}/companies/${this.id}`, formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              })
            : axios.post(`${url}/companies`, formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              });

        const response = await request;

        if (response.data.Logo) {
          this.formData.Logo = response.data.Logo;
        }

        this.showSuccess(
          `Company successfully ${
            this.action === "edit" ? "updated" : "created"
          }`
        );
        this.$router.push("/companies");
      } catch (error) {
        this.showError(
          error.response?.data?.msg || "Error saving company data"
        );
        console.error("Error submitting form:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    handleLogoChange(file) {
      if (file) {
        this.logoFile = file;
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.logoFile = null;
        this.previewUrl = null;
      }
    },

    handleCancel() {
      if (this.hasChanges && !this.isViewMode) {
        this.confirmDialog("Are you sure you want to discard changes?", () =>
          this.$router.push("/companies")
        );
      } else {
        this.$router.push("/companies");
      }
    },

    goBack() {
      if (this.hasChanges && !this.isViewMode) {
        this.confirmDialog("Are you sure you want to discard changes?", () =>
          this.$router.go(-1)
        );
      } else {
        this.$router.go(-1);
      }
    },

    confirmDialog(message, callback) {
      this.confirmDialogMessage = message;
      this.confirmActionCallback = callback;
      this.showConfirmDialog = true;
    },

    confirmAction() {
      if (this.confirmActionCallback) {
        this.confirmActionCallback();
      }
      this.showConfirmDialog = false;
    },

    showSuccess(message) {
      this.text = message;
      this.snackbarColor = "success";
      this.msg = true;
    },

    showError(message) {
      this.text = message;
      this.snackbarColor = "error";
      this.msg = true;
    },
  },
};
</script>

<style scoped>
.form-container {
  max-width: 1200px;
  margin: 0 auto;
}

.form-card {
  border-radius: 0px !important;
}

.sticky-header {
  position: sticky;
  top: 61px;
  z-index: 100;
  background: white;
}

.sticky-header.scrolled {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-gradient {
  background: linear-gradient(135deg, #2b3990 0%, #1a237e 100%);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #2b3990;
  border-bottom: 2px solid #e0e0e0;
}

.v-text-field {
  transition: all 0.3s ease;
}

.v-text-field:hover:not(.v-input--is-disabled) {
  transform: translateY(-1px);
}

.v-btn {
  text-transform: none;
}

.error--text {
  color: #ff5252;
}

.logo-upload-container {
  border: 2px dashed #e0e0e0;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: border-color 0.3s ease;
}

.logo-upload-container:hover {
  border-color: #2b3990;
}

.empty-logo-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  background: #f5f5f5;
  border-radius: 4px;
}

.logo-input {
  max-width: 300px;
  margin: 0 auto;
}

.v-img {
  border-radius: 4px;
  background: #f5f5f5;
  padding: 1rem;
}
</style>
